import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
  favoritesCount: 0,
  groupsCount: 0,
  groupsUnreadCount: 0,
  mineUnreadCount: 0,
  unAssignedUnreadCount: 0,
  allUnreadCount: 0,
  favoritesUnreadCount: 0,
  // Account-wide total for the sidebar badge. Kept separate from allUnreadCount,
  // which is scoped to the currently applied inbox/team/status filters.
  sidebarTotalUnreadCount: 0,
  inboxUnreadCounts: {},
  teamUnreadCounts: {},
  // Unread for the "all my teams" folder, per inbox.
  myTeamsUnreadCounts: {},
  chatbotUnreadCounts: {},
};

export const getters = {
  getStats: $state => $state,
  getInboxUnreadCounts: $state => $state.inboxUnreadCounts,
  getTeamUnreadCounts: $state => $state.teamUnreadCounts,
  getMyTeamsUnreadCounts: $state => $state.myTeamsUnreadCounts,
  getChatbotUnreadCounts: $state => $state.chatbotUnreadCounts,
  getTotalUnreadCount: $state => $state.allUnreadCount,
  getInboxUnreadCountFor: (_state, storeGetters) => inboxId =>
    storeGetters.getInboxUnreadCounts[String(inboxId)] || 0,
  getTeamUnreadCountFor: (_state, storeGetters) => (inboxId, teamId) =>
    storeGetters.getTeamUnreadCounts[`${inboxId}:${teamId}`] || 0,
  getSidebarTotalUnreadCount: $state => $state.sidebarTotalUnreadCount || 0,
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    // ignore
  }
};

const debouncedFetchSidebarStats = debounce(
  commit => fetchMetaData(commit, {}),
  500,
  false,
  1500
);

const debouncedFetchMetaData = debounce(fetchMetaData, 500, false, 1500);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 5000, false, 10000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  10000,
  false,
  20000
);

export const actions = {
  fetchSidebarStats: async ({ commit }) => {
    try {
      const response = await ConversationApi.meta({});
      const {
        data: { meta },
      } = response;
      // Only sidebar badge fields — do not overwrite inbox-scoped tab counts.
      if (meta) {
        commit(types.SET_CONV_TAB_META, {
          inbox_unread_counts: meta.inbox_unread_counts,
          team_unread_counts: meta.team_unread_counts,
          my_teams_unread_counts: meta.my_teams_unread_counts,
          chatbot_unread_counts: meta.chatbot_unread_counts,
          sidebar_total_unread_count: meta.all_unread_count,
        });
      }
    } catch (error) {
      // ignore
    }
  },
  fetchScopedMeta: async ({ commit }, params) => {
    await fetchMetaData(commit, params || {});
  },
  get: async ({ commit, state: $state }, params) => {
    if ($state.allCount > 5000) {
      superLongDebouncedFetchMetaData(commit, params);
    } else if ($state.allCount > 100) {
      longDebouncedFetchMetaData(commit, params);
    } else {
      debouncedFetchMetaData(commit, params);
    }
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
  adjustUnreadOnRead(
    { commit, state: unreadState },
    { inboxId, teamId, amount, isMine }
  ) {
    if (!amount) return;

    const inboxKey = String(inboxId);
    const teamKey = teamId ? `${inboxId}:${teamId}` : null;
    const nextInboxCounts = { ...unreadState.inboxUnreadCounts };
    const nextTeamCounts = { ...unreadState.teamUnreadCounts };
    const nextMyTeamCounts = { ...unreadState.myTeamsUnreadCounts };
    nextMyTeamCounts[inboxKey] = Math.max(
      0,
      (nextMyTeamCounts[inboxKey] || 0) - amount
    );

    nextInboxCounts[inboxKey] = Math.max(
      0,
      (nextInboxCounts[inboxKey] || 0) - amount
    );

    if (teamKey) {
      nextTeamCounts[teamKey] = Math.max(
        0,
        (nextTeamCounts[teamKey] || 0) - amount
      );
    }

    const meta = {
      inbox_unread_counts: nextInboxCounts,
      team_unread_counts: nextTeamCounts,
      my_teams_unread_counts: nextMyTeamCounts,
      all_unread_count: Math.max(0, (unreadState.allUnreadCount || 0) - amount),
      sidebar_total_unread_count: Math.max(
        0,
        (unreadState.sidebarTotalUnreadCount || 0) - amount
      ),
    };

    if (isMine) {
      meta.mine_unread_count = Math.max(
        0,
        (unreadState.mineUnreadCount || 0) - amount
      );
    }

    commit(types.SET_CONV_TAB_META, meta);
  },
  fetchSidebarStatsDebounced({ commit }) {
    debouncedFetchSidebarStats(commit);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
      favorites_count: favoritesCount,
      groups_count: groupsCount,
      groups_unread_count: groupsUnreadCount,
      mine_unread_count: mineUnreadCount,
      unassigned_unread_count: unAssignedUnreadCount,
      all_unread_count: allUnreadCount,
      favorites_unread_count: favoritesUnreadCount,
      sidebar_total_unread_count: sidebarTotalUnreadCount,
      inbox_unread_counts: inboxUnreadCounts,
      team_unread_counts: teamUnreadCounts,
      my_teams_unread_counts: myTeamsUnreadCounts,
      chatbot_unread_counts: chatbotUnreadCounts,
    } = {}
  ) {
    if (mineCount !== undefined) $state.mineCount = mineCount;
    if (allCount !== undefined) $state.allCount = allCount;
    if (unAssignedCount !== undefined) $state.unAssignedCount = unAssignedCount;
    if (favoritesCount !== undefined) $state.favoritesCount = favoritesCount;
    if (groupsCount !== undefined) $state.groupsCount = groupsCount;
    if (groupsUnreadCount !== undefined)
      $state.groupsUnreadCount = groupsUnreadCount;
    if (mineUnreadCount !== undefined) $state.mineUnreadCount = mineUnreadCount;
    if (unAssignedUnreadCount !== undefined) {
      $state.unAssignedUnreadCount = unAssignedUnreadCount;
    }
    if (allUnreadCount !== undefined) $state.allUnreadCount = allUnreadCount;
    if (favoritesUnreadCount !== undefined) {
      $state.favoritesUnreadCount = favoritesUnreadCount;
    }
    if (sidebarTotalUnreadCount !== undefined) {
      $state.sidebarTotalUnreadCount = sidebarTotalUnreadCount;
    }
    if (inboxUnreadCounts !== undefined) {
      $state.inboxUnreadCounts = inboxUnreadCounts;
    }
    if (teamUnreadCounts !== undefined) {
      $state.teamUnreadCounts = teamUnreadCounts;
    }
    if (myTeamsUnreadCounts !== undefined) {
      $state.myTeamsUnreadCounts = myTeamsUnreadCounts;
    }
    if (chatbotUnreadCounts !== undefined) {
      $state.chatbotUnreadCounts = chatbotUnreadCounts;
    }
    $state.updatedOn = new Date();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
